import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/components/toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import type { PartyPc } from "@/lib/core/schemas/party-view";
import { callMcpTool } from "@/src/api/mcp";

export function SpellbookPanel({
  pc,
  campaignId,
  onChange,
}: {
  pc: PartyPc | null;
  campaignId: string;
  onChange: () => unknown;
}) {
  const { sheet, isLoading, error, mutate } = useCharacterSheet(
    campaignId,
    pc?.id ?? null
  );
  const preparation = sheet?.spellPreparation;
  const preparedIds = useMemo(
    () =>
      preparation?.entries
        .filter((entry) => entry.prepared)
        .map((entry) => entry.actionId) ?? [],
    [preparation]
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(preparedIds)
  );
  const lastPreparedIdsRef = useRef<string[]>(preparedIds);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const isSame =
      preparedIds.length === lastPreparedIdsRef.current.length &&
      preparedIds.every(
        (id, index) => id === lastPreparedIdsRef.current[index]
      );
    if (!isSame) {
      lastPreparedIdsRef.current = preparedIds;
      setSelectedIds(new Set(preparedIds));
    }
  }, [preparedIds]);

  if (!pc) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        No character selected.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Loading spellbook...
      </div>
    );
  }
  if (error || !preparation) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        No directly carried Wizard spellbook is available.
      </div>
    );
  }

  const changed =
    selectedIds.size !== preparedIds.length ||
    preparedIds.some((actionId) => !selectedIds.has(actionId));

  const toggle = (actionId: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (
        checked &&
        !current.has(actionId) &&
        current.size >= preparation.limit
      ) {
        toast({
          type: "error",
          description: `You can prepare at most ${preparation.limit} spells.`,
        });
        return current;
      }
      const next = new Set(current);
      if (checked) {
        next.add(actionId);
      } else {
        next.delete(actionId);
      }
      return next;
    });
  };

  const commit = async () => {
    setIsSaving(true);
    try {
      await callMcpTool(
        "prepare_spells",
        { actorId: pc.id, spellIds: Array.from(selectedIds) },
        campaignId
      );
      await mutate();
      await onChange();
    } catch (err) {
      toast({
        type: "error",
        description:
          err instanceof Error ? err.message : "Preparing spells failed.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="space-y-4 pb-16">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-foreground">Spellbook</h3>
          <p className="text-muted-foreground text-sm">
            {selectedIds.size}/{preparation.limit} prepared
          </p>
        </div>
        {!preparation.canPrepare && <Badge variant="secondary">Prepared</Badge>}
      </div>

      {preparation.entries.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground text-sm">
          This spellbook has no spells to prepare.
        </p>
      ) : (
        <ul className="divide-y rounded-md border border-border">
          {preparation.entries.map((entry) => {
            const selected = selectedIds.has(entry.actionId);
            return (
              <li className="flex items-start gap-3 p-3" key={entry.actionId}>
                <Checkbox
                  aria-label={`Prepare ${entry.label}`}
                  checked={selected}
                  disabled={!preparation.canPrepare || isSaving}
                  onCheckedChange={(checked) =>
                    toggle(entry.actionId, checked === true)
                  }
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground text-sm">
                      {entry.label}
                    </span>
                    {entry.prepared && (
                      <Badge variant="secondary">Prepared</Badge>
                    )}
                  </div>
                  {entry.description && (
                    <p className="text-muted-foreground text-sm">
                      {entry.description}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {preparation.canPrepare ? (
        <Button disabled={!changed || isSaving} onClick={commit} type="button">
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Prepare Spells
        </Button>
      ) : (
        <p className="text-muted-foreground text-sm">
          {preparation.unavailableReason}
        </p>
      )}
    </section>
  );
}
